/**
 * Paging for Argus lists - the Inbox's cards and the Rules table.
 *
 * Client-side on purpose: both lists arrive whole from one request, and at the
 * sizes Argus works at (tens to low hundreds) slicing in the browser is instant
 * and keeps filters, counts and paging in agreement. The page is clamped here,
 * so a list that shrinks under an open page (a filter, an acknowledge, a
 * refresh) lands on its last page rather than an empty one.
 */

import { html, nothing, type TemplateResult } from 'lit';

export interface Page<T> {
  items: T[];
  /** Zero-based, after clamping. */
  page: number;
  pages: number;
  /** One-based positions of the first and last item shown, for "1-24 of 51". */
  from: number;
  to: number;
  total: number;
}

/** The slice of `items` for `page`, with the page clamped to what exists. */
export function paginate<T>(items: T[], page: number, size: number): Page<T> {
  const pages = Math.max(1, Math.ceil(items.length / size));
  const at = Math.min(Math.max(0, page), pages - 1);
  const start = at * size;
  const slice = items.slice(start, start + size);
  return {
    items: slice,
    page: at,
    pages,
    from: slice.length ? start + 1 : 0,
    to: start + slice.length,
    total: items.length,
  };
}

/** Page numbers to offer: first, last, and two either side of the current, with gaps. */
export function pageNumbers(page: number, pages: number): Array<number | null> {
  const wanted = new Set([0, pages - 1, page - 2, page - 1, page, page + 1, page + 2]);
  const out: Array<number | null> = [];
  let last = -1;
  for (let i = 0; i < pages; i++) {
    if (!wanted.has(i)) continue;
    if (last >= 0 && i - last > 1) out.push(null);
    out.push(i);
    last = i;
  }
  return out;
}

/** A rows-per-page choice, for lists people page through at their own pace. */
export interface PageSizeChoice {
  value: number;
  options: number[];
  set: (size: number) => void;
}

/**
 * "1-24 of 51" and the page buttons. Nothing at all while everything fits on
 * one page - a pager with one page is chrome for its own sake - unless there
 * is a page size to choose and a smaller one would page: then the choice
 * stays, or picking a large size would hide the way back.
 */
export function renderPager<T>(
  p: Page<T>,
  noun: string,
  go: (page: number) => void,
  size?: PageSizeChoice,
): TemplateResult | typeof nothing {
  const sizeMatters = !!size && p.total > Math.min(...size.options);
  if (p.pages <= 1 && !sizeMatters) return nothing;
  return html`<nav class="argus-pager" aria-label=${`${noun} pages`}>
    <span class="argus-pager-range">${p.from}–${p.to} of ${p.total} ${noun}</span>
    ${size && sizeMatters
      ? html`<label class="argus-pager-size">
          <span>Per page</span>
          <select class="argus-pager-btn" @change=${(e: Event) => size.set(Number((e.target as HTMLSelectElement).value))}>
            ${size.options.map((n) => html`<option value=${n} ?selected=${n === size.value}>${n}</option>`)}
          </select>
        </label>`
      : nothing}
    ${p.pages <= 1 ? nothing : renderButtons(p, go)}
  </nav>`;
}

function renderButtons<T>(p: Page<T>, go: (page: number) => void): TemplateResult {
  return html`
    <button type="button" class="argus-pager-btn" aria-label="Previous page" ?disabled=${p.page === 0}
      @click=${() => go(p.page - 1)}>‹</button>
    ${pageNumbers(p.page, p.pages).map((n) =>
      n === null
        ? html`<span class="argus-pager-gap" aria-hidden="true">…</span>`
        : html`<button type="button" class="argus-pager-btn ${n === p.page ? 'is-on' : ''}"
            aria-current=${n === p.page ? 'page' : 'false'} @click=${() => go(n)}>${n + 1}</button>`,
    )}
    <button type="button" class="argus-pager-btn" aria-label="Next page" ?disabled=${p.page >= p.pages - 1}
      @click=${() => go(p.page + 1)}>›</button>
  `;
}
